import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ROUTES } from "../../constant/routes";
import { LoginForm } from "../../components/ui/LoginForm";
import logo from "../../assets/llb360.png";
import { useAuthStore } from "../../context/useAuthStore";
import secureLocalStorage from "react-secure-storage";

const ROLES = {
  student: String(import.meta.env.VITE_STUDENT_ID).trim(),
  productTeam: String(import.meta.env.VITE_PRODUCT_TEAM_ID).trim(),
  admin: String(import.meta.env.VITE_ADMIN_ID).trim(),
  superAdmin: String(import.meta.env.VITE_SUPER_ADMIN_ID).trim(),
};

const QR_VISIT_FLAG_KEY = "llb360_qr_visit_logged";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const detectDeviceType = () => {
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "Tablet";
  if (/mobi|android|iphone|ipod/i.test(ua)) return "Mobile";
  return "Desktop";
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuthStore();

  useEffect(() => {
    if (user?.roleId) redirectToDashboard(user.roleId);
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isQrCodeView = params.get("is_qrcode_view") === "true";
    const alreadyLogged = secureLocalStorage.getItem(QR_VISIT_FLAG_KEY);

    if (!isQrCodeView || alreadyLogged) return;

    const logQrVisit = async () => {
      let ipAddress = "";
      let locationName = "Unknown";

      try {
        const res = await axios.get("https://ipapi.co/json/");
        ipAddress = res.data?.ip ?? "";
        locationName = `${res.data?.region ?? "Unknown"} - ${
          res.data?.city ?? "Unknown"
        }`;
      } catch {
        /* empty catch */
      }

      try {
        const payload = {
          ipAddress,
          deviceType: detectDeviceType(),
          location: locationName,
        };
        await axios.post(`${API_BASE_URL}/user/qrvisits`, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        secureLocalStorage.setItem(QR_VISIT_FLAG_KEY, "true");
      } catch {
        /* empty catch */
      }
    };

    void logQrVisit();
  }, [location.search, token]);

  const redirectToDashboard = (roleId: string) => {
    const role = String(roleId).trim();

    if ([ROLES.student, ROLES.productTeam].includes(role)) {
      navigate(ROUTES.DASHBOARD, { replace: true });
      return;
    }

    if (role === ROLES.admin) {
      navigate(ROUTES.ADMIN_DASHBOARD, { replace: true });
      return;
    }

    if (role === ROLES.superAdmin) {
      navigate(ROUTES.SUPERADMIN_DASHBOARD, { replace: true });
      return;
    }
  };

  return (
    <main className="authLayoutMain min-h-[calc(100vh-theme(space.8))] h-auto pb-4 no-scrollbar">
      <section className="min-h-[calc(100vh-theme(space.32))] h-auto pb-4 flex items-center justify-center lg:flex-row flex-col-reverse gap-4 p-4 no-scrollbar">
        <div className="w-full max-w-xl shadow-md p-2.5 rounded-xl bg-white border border-myInputBorder/80">
          <iframe
            className="rounded-md"
            width="100%"
            height="300rem"
            src="https://www.youtube.com/embed/yhBXu55xlkY?si=WW1bGdu0Dk9px3HA"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </div>

        <div className="w-full max-w-xl shadow-md p-4 rounded-xl bg-white border border-myInputBorder/80">
          <Link to={ROUTES.HOME} className="flex justify-center w-full">
            <img src={logo} alt="LLB360 Logo" className="w-20" loading="lazy"/>
          </Link>

          <h2 className="authSubtitle">User Login</h2>

          <LoginForm onSuccess={redirectToDashboard} />
        </div>
      </section>

      <footer className="flex flex-row gap-4 items-center justify-center w-full">
        {footerMenu.map((item, index) => (
          <Link key={index} to={item.route} className="forgotPasswordLink">
            {item.name}
          </Link>
        ))}
      </footer>
    </main>
  );
};

export default LoginPage;

const footerMenu = [
  { name: "Contact Us", route: ROUTES.CONTACT_US },
  { name: "Privacy Policy", route: ROUTES.PRIVACY_POLICY },
  { name: "Terms and Conditions", route: ROUTES.TERMS_AND_CONDITIONS },
  { name: "Refund Policy", route: ROUTES.REFUND_POLICY },
  { name: "Shipping Policy", route: ROUTES.SHIPPING_POLICY },
  { name: "Disclaimer", route: ROUTES.DISCLAIMER },
];
